'use client';
import React, { useState, useEffect, useRef } from 'react';
import { TrendingUp, AlertTriangle, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { DailySoaringForecast } from '@/lib/mockWeatherData';
import { fetchSoaringForecast } from '@/lib/openMeteoApi';
import WeekComparisonChart from './WeekComparisonChart';
import PilotForecastMatrix from './PilotForecastMatrix';
import { useAerodrome } from '@/context/AerodromeContext';
import { useSettings } from '@/context/SettingsContext';

export default function MultiDayContent() {
  const { selectedAerodrome, hydrated } = useAerodrome();
  const { formatAltitude } = useSettings();
  const [forecasts, setForecasts] = useState<DailySoaringForecast[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const lastManualRefreshRef = useRef(0);

  async function loadForecasts() {
    const requestId = requestIdRef.current + 1;
    if (!hydrated) return;
    requestIdRef.current = requestId;
    const requestAerodromeKey = `${selectedAerodrome.icao}-${selectedAerodrome.lat}-${selectedAerodrome.lon}`;
    setLoading(true);
    setError(null);
    setForecasts([]);
    try {
      const { week } = await fetchSoaringForecast(selectedAerodrome);
      const currentAerodromeKey = `${selectedAerodrome.icao}-${selectedAerodrome.lat}-${selectedAerodrome.lon}`;
      if (requestId !== requestIdRef.current || requestAerodromeKey !== currentAerodromeKey) return;
      setForecasts(week);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.warn('MultiDay fetch warning:', err);
      const message = err instanceof Error && err.message.includes('limite')
        ? 'Open-Meteo limite temporairement les requetes. Les dernieres donnees disponibles sont conservees.'
        : 'Impossible de charger les previsions 7 jours.';
      setError(message);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }

  useEffect(() => {
    if (!hydrated) return;
    loadForecasts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, selectedAerodrome.icao, selectedAerodrome.lat, selectedAerodrome.lon, selectedAerodrome.country]);

  function handleRefresh() {
    const now = Date.now();
    const elapsed = now - lastManualRefreshRef.current;
    if (elapsed < 45_000) return;
    lastManualRefreshRef.current = now;
    loadForecasts();
  }

  const bestDay = forecasts.length > 0
    ? forecasts.reduce((a, b) => a.qualityScore > b.qualityScore ? a : b)
    : null;
  const stormDays = forecasts.filter(f => f.stormRisk > 45).length;
  const bestCuBase = forecasts.length > 0 ? Math.max(...forecasts.map(f => f.maxCumulusBase)) : 0;
  const bestCuBaseFmt = bestCuBase > 0 ? formatAltitude(bestCuBase) : null;

  const dateRange = forecasts.length >= 2
    ? `${forecasts[0]?.date} -> ${forecasts[forecasts.length - 1]?.date}`
    : '';
  const lastUpdatedLabel = forecasts[0]?.lastUpdated
    ? new Date(forecasts[0].lastUpdated).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })
    : null;
  const cacheLabel =
    forecasts[0]?.cacheStatus === 'rate-limited'
      ? 'Actualisation temporairement bloquee par Open-Meteo'
      : forecasts[0]?.cacheStatus === 'cached' || forecasts[0]?.cacheStatus === 'stale'
        ? 'Donnees mises en cache'
        : forecasts.length > 0
          ? 'Donnees fraiches'
          : '';

  return (
    <div className="min-h-screen px-4 md:px-6 xl:px-8 2xl:px-10 py-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Prévisions 7 jours</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
            {selectedAerodrome.icao} - {selectedAerodrome.name} - {dateRange} - {forecasts[0]?.modelSourceLabel ?? 'Open-Meteo'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {bestDay && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs"
              style={{ backgroundColor: 'color-mix(in srgb, var(--success-green) 10%, var(--card))', border: '1px solid color-mix(in srgb, var(--success-green) 28%, var(--card-border))' }}
            >
              <TrendingUp size={13} style={{ color: 'var(--success-green)' }} />
              <span style={{ color: 'var(--success-green)' }}>Meilleur jour : <strong>{bestDay.dayLabel}</strong> ({bestDay.qualityScore}/100)</span>
            </div>
          )}
          {stormDays > 0 && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs"
              style={{ backgroundColor: 'color-mix(in srgb, var(--danger-red) 10%, var(--card))', border: '1px solid color-mix(in srgb, var(--danger-red) 28%, var(--card-border))' }}
            >
              <AlertTriangle size={13} style={{ color: 'var(--danger-red)' }} />
              <span style={{ color: 'var(--danger-red)' }}>{stormDays} jour{stormDays > 1 ? 's' : ''} à risque orageux</span>
            </div>
          )}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all active:scale-95 disabled:opacity-50"
            style={{ backgroundColor: 'var(--surface-elevated)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
        </div>
      </div>

      {forecasts.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
          <span className="px-2.5 py-1 rounded-lg" style={{ backgroundColor: 'var(--surface-elevated)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
            {cacheLabel}
          </span>
          {lastUpdatedLabel && (
            <span className="px-2.5 py-1 rounded-lg" style={{ backgroundColor: 'var(--surface-elevated)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
              Derniere mise a jour : {lastUpdatedLabel}
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center gap-4 py-24">
          <Loader2 size={32} className="animate-spin" style={{ color: 'var(--accent-cyan)' }} />
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Chargement des prévisions Open-Meteo...</p>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center gap-4 py-16 rounded-2xl"
          style={{ backgroundColor: 'var(--card)', border: '1px solid color-mix(in srgb, var(--danger-red) 30%, var(--card-border))' }}>
          <AlertCircle size={40} style={{ color: 'var(--danger-red)' }} />
          <p className="text-sm" style={{ color: 'var(--danger-red)' }}>{error}</p>
          <button onClick={handleRefresh} className="px-4 py-2 rounded-xl text-sm font-medium"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--accent-cyan) 12%, transparent)',
              border: '1px solid color-mix(in srgb, var(--accent-cyan) 34%, transparent)',
              color: 'var(--accent-cyan)',
            }}>
            Réessayer
          </button>
        </div>
      ) : (
        <>
          {/* Week comparison chart */}
          <div
            className="rounded-xl p-5 mb-4"
            style={{ backgroundColor: 'var(--surface-elevated)', border: '1px solid var(--border)' }}
          >
            <WeekComparisonChart forecasts={forecasts} />
          </div>

          {/* Summary stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {[
              {
                id: 'stat-flyable',
                label: 'Bonnes journees +',
                value: forecasts.filter(f => f.qualityScore >= 50).length.toString(),
                color: 'var(--success-green)',
              },
              {
                id: 'stat-marginal',
                label: 'Journees moyennes',
                value: forecasts.filter(f => f.qualityScore >= 15 && f.qualityScore < 50).length.toString(),
                color: 'var(--warning-orange)',
              },
              {
                id: 'stat-storm',
                label: 'Risque orageux',
                value: `${stormDays} jour${stormDays > 1 ? 's' : ''}`,
                color: 'var(--danger-red)',
              },
              {
                id: 'stat-best-cbh',
                label: 'Meilleure base Cu',
                value: bestCuBaseFmt ? `${bestCuBaseFmt.primary}${bestCuBaseFmt.primaryUnit}` : '--',
                color: 'var(--purple-cloud)',
              },
            ].map((stat) => (
              <div
                key={stat.id}
                className="rounded-xl px-4 py-3"
                style={{ backgroundColor: 'var(--surface-elevated)', border: '1px solid var(--border)' }}
              >
                <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--text-muted)', letterSpacing: '0.08em' }}>
                  {stat.label}
                </p>
                <p className="font-mono-data text-2xl font-bold" style={{ color: stat.color }}>
                  {stat.value}
                </p>
              </div>
            ))}
          </div>

          <div className="mb-4">
            <PilotForecastMatrix forecasts={forecasts} />
          </div>
        </>
      )}
    </div>
  );
}
